import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PillTag } from '@/components/pill-tag';
import { PricingCard } from '@/components/card/pricing-card';
import { useTranslation } from 'react-i18next';
import { FaqSection } from '@/routes/console/_authenticated/_layout/campaigns/$publicId/_layout/-features/faq-section';
import { PlanComparisonTable } from '@/components/plan-comparison-table';
import { motion } from 'framer-motion';
import { container, fadeUp } from '@/constants/motion';
import { getPlans } from '@/billing/plan';
import { cn } from '@/lib/utils';
import { ShieldCheck, RefreshCw, Gift, Sparkles } from 'lucide-react';
import { CampaignPagePreview } from '@/components/campaign-page-preview';
import { CampaignModesSpotlight } from '@/components/campaign-modes-spotlight';
export const Route = createFileRoute('/_layout/pricing/')({
    component: PricingPage,
    head: () => ({
        meta: [
            { title: 'Pricing — Adora Analytics' },
            {
                name: 'description',
                content: 'Start free with 2 campaigns. Upgrade to Solo or Team for unlimited campaigns, A/B testing, and advanced analytics. No credit card required.',
            },
            { property: 'og:title', content: 'Pricing — Adora Analytics' },
            {
                property: 'og:description',
                content: 'Start free with 2 campaigns. Upgrade to Solo or Team for unlimited campaigns, A/B testing, and advanced analytics. No credit card required.',
            },
        ],
    }),
});
function PricingPage() {
    const { t } = useTranslation('marketing');
    const [billing, setBilling] = React.useState('annual');
    const plans = getPlans(billing);
    return (<div className="min-h-screen bg-background text-foreground">
            {/* HERO */}
            <motion.section className="relative overflow-hidden border-b bg-linear-to-b from-primary/6 to-background" initial="hidden" animate="visible" variants={container}>
                <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-125 w-125 rounded-full bg-primary/8 blur-3xl"/>
                <div className="relative mx-auto max-w-4xl px-4 py-20 md:py-28 text-center">
                    <motion.div variants={fadeUp}>
                        <PillTag label={t('common.labels.pricing', 'Pricing')}/>
                    </motion.div>
                    <motion.h1 variants={fadeUp} className="mt-6 text-4xl font-extrabold tracking-tight md:text-5xl lg:text-6xl">
                        {t('pricing.hero.title')}
                    </motion.h1>
                    <motion.p variants={fadeUp} className="mt-4 text-base text-muted-foreground md:text-lg max-w-xl mx-auto leading-relaxed">
                        {t('pricing.hero.subtitle')}
                    </motion.p>

                    {/* Hero trust strip */}
                    <motion.div variants={fadeUp} className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2">
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <ShieldCheck className="h-3.5 w-3.5 text-primary"/>
                            No credit card required
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <RefreshCw className="h-3.5 w-3.5 text-primary"/>
                            Cancel anytime
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Gift className="h-3.5 w-3.5 text-primary"/>2 free campaigns, always
                        </span>
                    </motion.div>
                </div>
            </motion.section>

            {/* Mode spotlight — same component as / and /features so the
            showroom + auto-store + custom-theme pitch is consistent
            across the marketing surfaces a visitor might land on first. */}
            <CampaignModesSpotlight />

            <div className="mx-auto max-w-6xl px-4 py-16">
                {/* PLANS */}
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={container}>
                    <motion.div className="text-center mb-8" variants={fadeUp}>
                        <h2 className="text-2xl font-bold md:text-3xl">{t('pricing.plansTitle')}</h2>
                        <p className="mt-2 text-sm text-muted-foreground md:text-base">{t('pricing.plansSubtitle')}</p>
                    </motion.div>

                    {/* Billing toggle */}
                    <motion.div className="flex justify-center mb-10" variants={fadeUp}>
                        <div className="inline-flex items-center gap-0.5 rounded-full bg-muted p-1">
                            <button className={cn('rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200', billing === 'monthly'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground')} onClick={() => setBilling('monthly')}>
                                Monthly
                            </button>
                            <button className={cn('inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200', billing === 'annual'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground')} onClick={() => setBilling('annual')}>
                                Annual
                                <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                                    2 months free
                                </span>
                            </button>
                        </div>
                    </motion.div>

                    <div className="relative grid gap-6 md:grid-cols-3 md:items-stretch">
                        <div className="pointer-events-none absolute inset-0 left-1/2 -translate-x-1/2 h-full w-80 rounded-full bg-primary/8 blur-3xl"/>
                        {plans.map((p, idx) => (<motion.div key={p.code} variants={fadeUp} transition={{ duration: 0.45, delay: 0.05 + idx * 0.07 }} className="h-full">
                                <PricingCard name={p.name} price={p.price} priceHint={p.priceHint} annualNote={p.annualNote} description={p.description} featuresLabel={p.featuresLabel} footerNote={p.footerNote} highlight={p.highlighted} items={p.items} cta={p.cta} themesBadge={p.themesBadge}/>
                            </motion.div>))}
                    </div>

                    <motion.p className="mt-6 text-center text-xs text-muted-foreground" variants={fadeUp} transition={{ duration: 0.4, delay: 0.25 }}>
                        {t('pricing.summaryNote')}
                    </motion.p>
                </motion.div>

                {/* VALUE ANCHOR */}
                <motion.div className="mt-14 mx-auto max-w-lg" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeUp} transition={{ duration: 0.5 }}>
                    <div className="rounded-2xl border bg-muted/40 px-8 py-6 text-center">
                        <div className="mb-3 flex justify-center">
                            <Sparkles className="h-5 w-5 text-primary"/>
                        </div>
                        <p className="text-sm font-semibold">{t('pricing.valueAnchor.headline')}</p>
                        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                            {t('pricing.valueAnchor.body')}
                        </p>
                    </div>
                </motion.div>

                {/* THEMES BY PLAN — per-plan breakdown of what's unlocked.
            Sits right after the plan cards so the themes/animations
            badge on each card has a visual + copy explanation. Free
            vs Solo+ side-by-side; Team inherits Solo+'s pool, so the
            third column would be redundant — instead the Solo+
            column is captioned for both. */}
                <motion.div className="mt-20" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={fadeUp} transition={{ duration: 0.5 }}>
                    <div className="text-center mb-10">
                        <PillTag label={t('pricing.themes.pill', { defaultValue: 'Themes & animations' })}/>
                        <h2 className="mt-4 text-2xl font-bold md:text-3xl">
                            {t('pricing.themes.title', { defaultValue: "What's unlocked on each plan" })}
                        </h2>
                        <p className="mt-2 text-sm text-muted-foreground md:text-base max-w-2xl mx-auto">
                            {t('pricing.themes.subtitle', {
            defaultValue: "Every campaign picks a visual theme + an animated background. Here's exactly what each plan gets — and what's behind the Solo+ gate.",
        })}
                        </p>
                    </div>

                    <div className="grid gap-8 md:grid-cols-2">
                        <PlanThemesTier tier="free" badge="Free" title="15 themes · 15 animations" blurb="Clean, professional starting points. Minimal, focus, retro, classic — paired with calm ambient backgrounds (gradient flow, mesh, waves, blueprint, snow, rain)." previews={[
            { design: 'minimal', pattern: 'gradient-flow', caption: 'Minimal · Gradient' },
            { design: 'focus', pattern: 'mesh', caption: 'Focus · Mesh' },
            { design: 'classic', pattern: 'waves', caption: 'Classic · Waves' },
        ]} footnote="Enough to ship a polished campaign on day one. No upgrade required."/>
                        <PlanThemesTier tier="solo" badge="Solo & Team" title="35 themes · 42 animations" blurb="Adds 20 designer themes (aurora-borealis, cyberpunk, synthwave, glassmorphism, neo-brutal, fireworks…) and 27 pro patterns (plasma, vortex, galaxy, glitch, fireworks, confetti, petals…). Plus the random-pattern toggle for evergreen QRs." previews={[
            {
                design: 'aurora-borealis',
                pattern: 'aurora',
                caption: 'Aurora Borealis · Aurora',
            },
            { design: 'cyberpunk', pattern: 'glitch', caption: 'Cyberpunk · Glitch' },
            { design: 'fireworks', pattern: 'fireworks', caption: 'Fireworks · Fireworks' },
        ]} footnote="Solo and Team unlock the full catalogue. Same library, identical look." highlight/>
                    </div>

                    <p className="mt-6 text-center text-xs text-muted-foreground">
                        Theme + animation are decoupled — pick any combination. Pro gating is enforced server-side, so
                        the picker won't surprise you at save time.
                    </p>
                </motion.div>

                {/* PLAN COMPARISON TABLE */}
                <motion.div className="mt-20" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={fadeUp} transition={{ duration: 0.5 }}>
                    <PlanComparisonTable />
                </motion.div>

                {/* FAQ */}
                <motion.div className="mt-20" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={fadeUp} transition={{ duration: 0.5 }}>
                    <FaqSection />
                </motion.div>
            </div>
        </div>);
}
function PlanThemesTier({ badge, title, blurb, previews, footnote, highlight }) {
    return (<div className={cn('flex flex-col gap-6 rounded-2xl border p-6 md:p-8', highlight ? 'border-primary/40 bg-primary/5 shadow-sm' : 'bg-card')}>
            <div className="flex items-center gap-3">
                <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider', highlight ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground')}>
                    {badge}
                </span>
                <h3 className="text-base font-semibold md:text-lg">{title}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{blurb}</p>
            <div className="grid grid-cols-3 gap-3 items-end justify-items-center">
                {previews.map((p) => (<CampaignPagePreview key={`${p.design}-${p.pattern}`} design={p.design} pattern={p.pattern} mode="showroom" caption={p.caption} size="sm"/>))}
            </div>
            <p className="text-xs text-muted-foreground border-t pt-4 mt-auto">{footnote}</p>
        </div>);
}
